import React, { useEffect, useState } from 'react';
import { Activity, TrendingDown, TrendingUp } from 'lucide-react';
import { getMarketIndices, MarketIndexPayload } from '../../services/api';
import IndexMiniChart from './IndexMiniChart';

/** Fixed chart band height - keeps cards from growing with canvas/flex bugs.
 *  Mobile uses a smaller height to avoid dominating the viewport. */
const CHART_BAND_PX = 128;
const CHART_BAND_MOBILE_PX = 88;

const PLACEHOLDERS: MarketIndexPayload[] = [
  {
    key: 'dow',
    label: 'DOW',
    symbol: '^DJI',
    dateLabel: '-',
    last: null,
    change: null,
    changePercent: null,
    previousClose: null,
    relativeVolume: null,
    candles: [],
    error: null,
  },
  {
    key: 'nasdaq',
    label: 'NASDAQ',
    symbol: '^IXIC',
    dateLabel: '-',
    last: null,
    change: null,
    changePercent: null,
    previousClose: null,
    relativeVolume: null,
    candles: [],
    error: null,
  },
  {
    key: 'sp500',
    label: 'S&P 500',
    symbol: '^GSPC',
    dateLabel: '-',
    last: null,
    change: null,
    changePercent: null,
    previousClose: null,
    relativeVolume: null,
    candles: [],
    error: null,
  },
];

function formatSigned(n: number | null, digits = 2): string {
  if (n == null || Number.isNaN(n)) return '-';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}`;
}

const MarketIndicesRow: React.FC = () => {
  const [indices, setIndices] = useState<MarketIndexPayload[]>(PLACEHOLDERS);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      const onResize = () => setIsMobile(window.innerWidth < 768);
      onResize();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const mq = window.matchMedia('(max-width: 767px)');
    if (!mq || typeof mq.matches !== 'boolean') {
      setIsMobile(window.innerWidth < 768);
      return undefined;
    }

    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }

    // Legacy fallback for environments with older MediaQueryList API.
    if (typeof mq.addListener === 'function') {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }

    return undefined;
  }, []);

  const chartBandHeight = isMobile ? CHART_BAND_MOBILE_PX : CHART_BAND_PX;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (!cancelled) setFetchError(null);
        const data = await getMarketIndices();
        if (cancelled) return;
        if (data?.indices?.length) {
          setIndices(data.indices);
        }
      } catch (e: unknown) {
        if (cancelled) return;
        console.warn('Market indices fetch failed', e);
        setFetchError('Live index data unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const id = window.setInterval(load, 120_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <section
      className="market-indices-row py-2 py-md-3 border-bottom"
      style={{
        backgroundColor: 'var(--surface_container_low)',
        borderColor: 'var(--border-ghost)',
        overflow: 'hidden',
      }}
      aria-label="US market indices"
    >
      <div className="container">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <Activity
              size={18}
              style={{ color: 'var(--accent-primary)' }}
              aria-hidden
            />
            <span
              className="text-uppercase fw-semibold small"
              style={{ color: 'var(--text-muted)', letterSpacing: '0.06em' }}
            >
              Market indices
            </span>
            <span
              className="small d-md-none"
              style={{ color: 'var(--text-muted)' }}
            >
              · 4 mo
            </span>
            <span
              className="small d-none d-md-inline"
              style={{ color: 'var(--text-muted)' }}
            >
              Yahoo Finance · 4 mo (daily) · delayed
            </span>
          </div>
          {fetchError && (
            <span className="small" style={{ color: 'var(--accent-warning)' }}>
              {fetchError}
            </span>
          )}
        </div>

        <div className="row g-2">
          {indices.map((ix) => {
            const up = (ix.change ?? 0) >= 0;

            return (
              <div key={ix.key} className="col-12 col-lg-4">
                <div
                  className="market-index-card d-flex flex-column rounded-3 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--surface_container)',
                    border: '1px solid var(--border-ghost)',
                    maxWidth: '100%',
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between px-3 pt-2 pb-1 gap-2 flex-shrink-0">
                    <div>
                      <div className="d-flex align-items-baseline gap-2 flex-wrap">
                        <span
                          className="fw-bold"
                          style={{
                            color: 'var(--text-primary)',
                            fontFamily: 'var(--font-sans)',
                          }}
                        >
                          {ix.label}
                        </span>
                        <span
                          className="small"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          {ix.dateLabel || '-'}
                        </span>
                      </div>
                      {ix.error && (
                        <div
                          className="small mt-1"
                          style={{ color: 'var(--accent-bearish)' }}
                        >
                          {ix.error}
                        </div>
                      )}
                    </div>
                    <div className="text-end">
                      {ix.changePercent != null && (
                        <div
                          className="d-flex align-items-center justify-content-end gap-1 fw-semibold small"
                          style={{
                            color: up
                              ? 'var(--accent-bullish)'
                              : 'var(--accent-bearish)',
                          }}
                        >
                          {up ? (
                            <TrendingUp size={14} />
                          ) : (
                            <TrendingDown size={14} />
                          )}
                          {formatSigned(ix.change, 2)} (
                          {formatSigned(ix.changePercent, 2)}%)
                        </div>
                      )}
                      {ix.last != null && (
                        <div
                          className="mt-1 px-2 py-0 rounded-1 d-inline-block small fw-bold"
                          style={{
                            backgroundColor: 'var(--accent-primary)',
                            color: 'var(--on_primary)',
                            fontFamily: 'var(--font-mono)',
                          }}
                        >
                          {ix.last.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fixed-height chart band (min-width:0 + overflow stops flex blowout) */}
                  <div
                    className="d-flex flex-row align-items-stretch px-2 pb-2 flex-shrink-0"
                    style={{
                      height: chartBandHeight,
                      maxHeight: chartBandHeight,
                      minHeight: chartBandHeight,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      className="flex-grow-1"
                      style={{
                        minWidth: 0,
                        height: chartBandHeight,
                        maxHeight: chartBandHeight,
                        overflow: 'hidden',
                      }}
                    >
                      {!ix.error && ix.candles.length > 0 ? (
                        <IndexMiniChart
                          candles={ix.candles}
                          previousClose={ix.previousClose}
                          lastPrice={ix.last}
                          height={chartBandHeight}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center text-muted small h-100 w-100"
                          style={{ fontSize: '0.75rem' }}
                        >
                          {loading ? 'Loading…' : 'No chart data'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MarketIndicesRow;
